<script setup lang="ts">
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/ui/empty-state/EmptyState.vue';
import EntityRow from '@/components/workspace/EntityRow.vue';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import { BriefcaseBusiness, ChevronRight } from 'lucide-vue-next';
import type { ClientOverviewProjectCard } from './types';

interface Props {
    projects: ClientOverviewProjectCard[];
    canMutate: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: 'create-project'): void,
    (e: 'open-work'): void,
}>();
</script>

<template>
    <SectionSurface title="Current projects" description="A concise view of active delivery for this client." :icon="BriefcaseBusiness" density="compact" presentation="editorial" content-layout="flush">
        <EmptyState v-if="projects.length === 0" embedded title="No active projects yet" description="Linked delivery work will appear here.">
            <template v-if="canMutate" #actions><Button variant="outline" size="sm" @click="emit('create-project')">Create project</Button></template>
        </EmptyState>

        <div v-else>
            <EntityRow
                v-for="project in projects.slice(0, 3)"
                :key="project.id"
                :title="project.name"
                :description="project.nextAction"
                :meta="project.stageLabel"
                interactive
                @activate="navigateTo(project.href)"
            >
                <template #trailing><span class="hidden sm:inline">{{ project.progressLabel }}</span><ChevronRight class="size-4" /></template>
            </EntityRow>
        </div>
        <Button v-if="projects.length > 3" variant="ghost" size="sm" class="mt-4 px-0" @click="emit('open-work')">
            View all projects
        </Button>
    </SectionSurface>
</template>
